import React, { useRef, useState } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Badge,
  Popover,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  ShoppingBag as ShoppingBagIcon,
  Menu as MenuIcon,
} from "@mui/icons-material";
import Bag from "./Bag";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link, useLocation } from "react-router-dom";

const Header = ({ bag, removeFromBag }) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const location = useLocation();
  const isActive = (path) => location.pathname === path;
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleLogoOut = () => {
    logout();
    navigate("/logout");
  };

  const handleBagClick = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const toggleDrawer = (open) => {
    setDrawerOpen(open);
  };

  const handleMenuItemClick = (path) => {
    toggleDrawer(false);
    navigate(path);
    window.scrollTo({ top: 0 });
  };

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0 });
  };

  const menuItems = (
    <>
      <ListItem button onClick={() => handleMenuItemClick("/main")}>
        <ListItemText primary="Home" />
      </ListItem>
      <ListItem button onClick={() => handleMenuItemClick("/styles")}>
        <ListItemText primary="Our Styles" />
      </ListItem>
      <ListItem button onClick={() => handleMenuItemClick("/about-us")}>
        <ListItemText primary="About" />
      </ListItem>
      <ListItem button onClick={() => handleMenuItemClick("/contact-us")}>
        <ListItemText primary="Contact Us" />
      </ListItem>
    </>
  );

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: "#EAEAE9",
        borderBottom: "1px solid #ccc",
        height: "90px",
        boxShadow: "none",
        padding: { xs: "0px", md: "10px" },
      }}
    >
      <Toolbar sx={{ height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
            color: "#1d1d1f",
          }}
        >
          {/* Left side buttons for mobile hamburger */}
          {isMobile ? (
            <IconButton onClick={() => toggleDrawer(true)} color="inherit">
              <MenuIcon />
            </IconButton>
          ) : (
            <Box sx={{ display: "flex", gap: 2 }}>
              {isAuthenticated ? (
                <Button
                  color="inherit"
                  component={Link}
                  to="/main"
                  onClick={handleScrollToTop}
                >
                  Corporate Collection
                </Button>
              ) : (
                <>
                  <Button
                    color="inherit"
                    component={Link}
                    to="/main"
                    onClick={handleScrollToTop}
                    sx={{
                      textTransform: "capitalize",
                      fontWeight: isActive("/") ? "bold" : "normal",
                      textDecoration: isActive("/") ? "underline" : "none",
                    }}
                  >
                    Home
                  </Button>
                  <Button
                    color="inherit"
                    component={Link}
                    to="/styles"
                    onClick={handleScrollToTop}
                    sx={{
                      textTransform: "capitalize",
                      fontWeight: isActive("/styles") ? "bold" : "normal",
                      textDecoration: isActive("/styles")
                        ? "underline"
                        : "none",
                    }}
                  >
                    Our Styles
                  </Button>
                  <Button
                    color="inherit"
                    component={Link}
                    to="/about-us"
                    onClick={handleScrollToTop}
                    sx={{
                      textTransform: "capitalize",
                      fontWeight: isActive("/about-us") ? "bold" : "normal",
                      textDecoration: isActive("/about-us")
                        ? "underline"
                        : "none",
                    }}
                  >
                    About
                  </Button>

                  <Button
                    color="inherit"
                    component={Link}
                    to="/contact-us"
                    onClick={handleScrollToTop}
                    sx={{
                      textTransform: "capitalize",
                      fontWeight: isActive("/contact-us") ? "bold" : "normal",
                      textDecoration: isActive("/contact-us")
                        ? "underline"
                        : "none",
                    }}
                  >
                    Contact Us
                  </Button>
                </>
              )}
            </Box>
          )}

          {/* Logo in the center */}
          <Box
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <a href="/">
              <img
                src="/main-logo.png"
                alt="Main Logo"
                style={{
                  maxWidth: "150px",
                  height: "60px",
                }}
              />
            </a>
          </Box>

          {/* Right side buttons (login, account, etc.) */}
          <Box sx={{ display: "flex", gap: 2 }}>
            {isAuthenticated ? (
              <>
                <Button color="inherit" component={Link} to="/account">
                  Account
                </Button>

                <IconButton
                  onClick={handleBagClick}
                  color="inherit"
                  ref={anchorRef}
                >
                  <Badge badgeContent={bag.length} color="error">
                    <ShoppingBagIcon />
                  </Badge>
                </IconButton>

                <Button color="inherit" onClick={handleLogoOut}>
                  Logout
                </Button>

                {/* Bag dropdown (Popover) */}
                <Popover
                  open={dropdownOpen}
                  anchorEl={anchorRef.current}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  onClose={() => setDropdownOpen(false)}
                >
                  <Box sx={{ width: 300, p: 2 }}>
                    <Bag bag={bag} removeFromBag={removeFromBag} />
                  </Box>
                </Popover>
              </>
            ) : (
              <Button
                color="inherit"
                component={Link}
                disabled={true}
                to="/login"
              >
                {/* <img src={process.env.PUBLIC_URL + "/Login.svg"}></img> */}
              </Button>
            )}
          </Box>
        </Box>
      </Toolbar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => toggleDrawer(false)}
      >
        <Box sx={{ width: 150 }} role="presentation">
          <List>
            {menuItems}
            <Divider />
            {isAuthenticated && (
              <>
                <ListItem button onClick={handleLogoOut}>
                  <ListItemText primary="Logout" />
                </ListItem>
              </>
            )}
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Header;
